import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDataFetch } from "../../../hooks/use-data-fetch";

// Interfaces para los datos de la API
export interface Product {
  title: string;
  id: string;
  created_at: string;
}

export interface OrderItem {
  product_id: Product;
  quantity: number;
  price: number;
  influencer_bonus: number;
  loyalty_points: number;
  _id: string;
  id: string;
}

export interface Address {
  user_id: string;
  name: string;
  address: string;
  country_id: string;
  province_id: string;
  city_id: string;
  postal_code: string;
  neighborhood?: string;
  observations: string;
  status: boolean;
  created_at: string;
  createdAt: string;
  updatedAt: string;
  id: string;
}

export interface SiigoInvoice {
  id: string;
  document: {
    id: number;
  };
  prefix: string;
  number: number;
  name: string;
  date: string;
  customer: {
    id: string;
    identification: string;
    branch_office: number;
  };
  seller: number;
  total: number;
  balance: number;
  observations: string;
  items: any[];
  payments: any[];
  stamp?: {
    status: string;
    cufe: string;
  };
  mail?: {
    status: string;
    observations: string;
  };
  metadata: {
    created: string;
    last_updated: string;
  };
  public_url?: string;
  pdf_url?: string;
}

export interface Order {
  user_id: string;
  items: OrderItem[];
  address_id: Address;
  subtotal: number;
  total: number;
  status: string;
  is_paid: boolean;
  payment_provider?: string;
  payment_method?: string;
  delivery_type?: string;
  created_at: string;
  updated_at: string;
  currency: string | null;
  id: string;
  payment_proof?: string | null;
  payment_channel?: string;
  siigo_invoice?: SiigoInvoice;
  siigo_invoice_status?: string;
}

export interface OrdersResponse {
  message: string;
  data: {
    orders: Order[];
  };
  errors: any;
}

/**
 * Hook personalizado para manejar la lógica de órdenes
 */
export const useOrders = () => {
  // Estado para paginación y búsqueda
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [itemsPerPage] = useState(10);
  
  // Navegación para ir a la página de detalles
  const navigate = useNavigate();

  // Usar el hook para obtener los datos de las órdenes
  const { getData } = useDataFetch();
  const { data, isLoading, isError, error } = getData(
    ["orders"],
    "/order/byUser"
  );

  // Obtener las órdenes de la respuesta
  const orders = data?.data?.orders || [];

  // Filtrar órdenes basadas en la búsqueda
  const filteredOrders = orders.filter(
    (order: Order) =>
      order.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (order.items[0]?.product_id?.title || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Calcular valores de paginación
  const totalItems = filteredOrders.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedOrders = filteredOrders.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  // Función para cambiar de página
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Función para manejar cambios en la búsqueda
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Resetear a la primera página al buscar
  };

  // Función para obtener el color del badge según el estado
  

  // Función para navegar a la página de detalles de la orden
  const handleOrderClick = (order: Order) => {
    navigate(`/order/details/${order.id}`);
  };

  return {
    // Estados
    searchQuery,
    currentPage,
    itemsPerPage,
    
    // Datos
    orders,
    filteredOrders,
    paginatedOrders,
    totalItems,
    totalPages,
    startIndex,
    isLoading,
    isError,
    error,
    
    // Funciones
    handleSearchChange,
    handlePageChange,
    handleOrderClick
  };
};
